import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowUpRight, Check, Loader2, Lock } from "lucide-react";
import { toast } from "sonner";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import FileUpload from "@/components/FileUpload";
import api from "@/lib/api";
import useFormDraft from "@/lib/useFormDraft";
import { useUser } from "@/lib/userContext";

const TEAM_SIZES = ["Solo / Founder-led", "2 – 10", "11 – 50", "51 – 200", "200+"];
const REVENUE = ["Pre-revenue", "Under ₹50L", "₹50L – ₹2Cr", "₹2Cr – ₹10Cr", "₹10Cr – ₹50Cr", "₹50Cr+"];
const INVESTMENTS = [
    "Exploring",
    "₹1L – ₹3L / month",
    "₹3L – ₹7L / month",
    "₹7L – ₹15L / month",
    "₹15L+ / month",
];

const STEPS = [
    { key: "founder", label: "Founder" },
    { key: "business", label: "Business" },
    { key: "challenge", label: "Challenge" },
    { key: "vision", label: "Vision" },
    { key: "investment", label: "Investment" },
];

const INITIAL = {
    step: 0,
    files: [],
    form: {
        full_name: "",
        business_name: "",
        email: "",
        phone: "",
        location: "",
        industry: "",
        website: "",
        social_links: "",
        team_size: "",
        revenue_range: "",
        biggest_challenge: "",
        why_marketing_now: "",
        success_12mo: "",
        if_nothing_changes: "",
        investment_range: "",
        additional_notes: "",
    },
};

function Field({ label, children, span = "" }) {
    return (
        <div className={`flex flex-col ${span}`}>
            <span className="field-label">{label}</span>
            {children}
        </div>
    );
}

export default function Clients() {
    const navigate = useNavigate();
    const { user, signIn } = useUser();
    const [draft, setDraft, clearDraft] = useFormDraft("draft_clients", INITIAL);
    const { step, files, form } = draft;
    const setStep = (next) => setDraft((d) => ({ ...d, step: typeof next === "function" ? next(d.step) : next }));
    const setForm = (next) => setDraft((d) => ({ ...d, form: typeof next === "function" ? next(d.form) : next }));
    const setFiles = (next) => setDraft((d) => ({ ...d, files: typeof next === "function" ? next(d.files) : next }));

    const [busy, setBusy] = useState(false);
    const [tracking, setTracking] = useState(null);

    const set = (k) => (e) => setForm((s) => ({ ...s, [k]: e.target ? e.target.value : e }));

    const next = () => {
        const required = {
            0: ["full_name", "email", "phone", "location"],
            1: ["business_name", "industry", "team_size"],
            2: ["biggest_challenge", "why_marketing_now"],
            3: ["success_12mo", "if_nothing_changes"],
            4: ["revenue_range", "investment_range"],
        }[step];
        const missing = required.find((k) => !form[k]?.trim?.());
        if (missing) {
            toast.error("Please complete this section before continuing");
            return;
        }
        setStep((s) => Math.min(s + 1, STEPS.length - 1));
    };

    const submit = async () => {
        if (!user) {
            toast.message("Sign in to submit", { description: "We'll bring you right back to this form." });
            signIn("/clients");
            return;
        }
        setBusy(true);
        try {
            const payload = { ...form, file_paths: files.map((f) => f.id) };
            const { data } = await api.post("/applications/client", payload);
            setTracking(data.tracking_id);
            clearDraft();
            toast.success("Application submitted");
        } catch (err) {
            console.error(err);
            toast.error(err?.response?.data?.detail || "Could not submit");
        } finally {
            setBusy(false);
        }
    };

    if (tracking) {
        return (
            <main className="min-h-screen" data-testid="client-success">
                <Navbar />
                <section className="pt-40 pb-32">
                    <div className="max-w-3xl mx-auto px-6 lg:px-10 text-center">
                        <div className="inline-flex items-center justify-center w-12 h-12 border is-active mb-8">
                            <Check size={20} />
                        </div>
                        <h1 className="font-display text-4xl lg:text-5xl tracking-tighter mb-6">Application received.</h1>
                        <p className="text-white/65 text-lg mb-10 max-w-xl mx-auto">
                            Thank you for considering Aangan Theory as your growth partner. Our team will review and respond within 48 hours.
                        </p>
                        <div className="card-surface p-8 inline-block">
                            <div className="eyebrow mb-2">Your tracking ID</div>
                            <div className="font-mono text-2xl text-saffron-brand tracking-widest" data-testid="client-tracking-id">{tracking}</div>
                        </div>
                        <div className="mt-12 flex flex-wrap justify-center gap-3">
                            <button onClick={() => navigate(`/status/${tracking}`)} className="btn-primary" data-testid="client-track-btn">
                                Track Application <ArrowUpRight size={16} />
                            </button>
                            <button onClick={() => navigate("/my")} className="btn-secondary">Go to My Dashboard</button>
                        </div>
                    </div>
                </section>
                <Footer />
            </main>
        );
    }

    return (
        <main className="min-h-screen" data-testid="clients-page">
            <Navbar />

            <section className="pt-32 pb-16 lg:pt-44 lg:pb-20 border-b border-white/8">
                <div className="max-w-7xl mx-auto px-6 lg:px-10">
                    <div className="eyebrow mb-5 text-saffron-brand">For Brands · Strategic Qualification</div>
                    <h1 className="font-display text-5xl lg:text-7xl tracking-tighter leading-[0.95] mb-8 max-w-3xl">
                        Let's build what <span className="text-saffron-brand">matters</span>.
                    </h1>
                    <p className="text-white/65 text-lg max-w-2xl">
                        We take on a handful of long-term partners every quarter. This is not a contact form — it is a qualification. Please be specific. Nothing here commits you to anything.
                    </p>
                </div>
            </section>

            <section className="py-16 lg:py-24">
                <div className="max-w-5xl mx-auto px-6 lg:px-10">
                    <div className="flex flex-wrap items-center gap-2 mb-12">
                        {STEPS.map((s, i) => (
                            <button
                                key={s.key}
                                onClick={() => setStep(i)}
                                className={`flex items-center gap-2 px-3 py-2 text-xs uppercase tracking-widest transition-colors ${
                                    i === step ? "text-saffron-brand border-b border-saffron" : i < step ? "text-white/65" : "text-white/35"
                                }`}
                                data-testid={`client-step-${i}`}
                            >
                                <span className="font-mono">0{i + 1}</span>
                                <span>{s.label}</span>
                            </button>
                        ))}
                    </div>

                    <div className="card-surface p-8 lg:p-12">
                        {step === 0 && (
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-8">
                                <Field label="Full Name"><input className="field-input" value={form.full_name} onChange={set("full_name")} data-testid="cl-input-full_name" /></Field>
                                <Field label="Email"><input type="email" className="field-input" value={form.email} onChange={set("email")} data-testid="cl-input-email" /></Field>
                                <Field label="Phone"><input className="field-input" value={form.phone} onChange={set("phone")} data-testid="cl-input-phone" /></Field>
                                <Field label="Location"><input className="field-input" value={form.location} onChange={set("location")} placeholder="City, Country" data-testid="cl-input-location" /></Field>
                            </div>
                        )}
                        {step === 1 && (
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-8">
                                <Field label="Business Name"><input className="field-input" value={form.business_name} onChange={set("business_name")} data-testid="cl-input-business_name" /></Field>
                                <Field label="Industry"><input className="field-input" value={form.industry} onChange={set("industry")} data-testid="cl-input-industry" /></Field>
                                <Field label="Website"><input className="field-input" value={form.website} onChange={set("website")} placeholder="https://" data-testid="cl-input-website" /></Field>
                                <Field label="Social Links"><input className="field-input" value={form.social_links} onChange={set("social_links")} placeholder="Comma separated" data-testid="cl-input-social_links" /></Field>
                                <Field label="Team Size" span="md:col-span-2">
                                    <div className="flex flex-wrap gap-2">
                                        {TEAM_SIZES.map((t) => (
                                            <button key={t} type="button" onClick={() => setForm((s) => ({ ...s, team_size: t }))} className={`tag ${form.team_size === t ? "is-active" : ""}`} data-testid={`cl-team-${t}`}>{t}</button>
                                        ))}
                                    </div>
                                </Field>
                            </div>
                        )}
                        {step === 2 && (
                            <div className="space-y-8">
                                <Field label="Biggest Challenge"><textarea className="field-input" rows={4} value={form.biggest_challenge} onChange={set("biggest_challenge")} placeholder="What's actually stopping you?" data-testid="cl-input-biggest_challenge" /></Field>
                                <Field label="Why are you considering marketing now?"><textarea className="field-input" rows={4} value={form.why_marketing_now} onChange={set("why_marketing_now")} data-testid="cl-input-why_marketing_now" /></Field>
                            </div>
                        )}
                        {step === 3 && (
                            <div className="space-y-8">
                                <Field label="What would success look like in 12 months?"><textarea className="field-input" rows={4} value={form.success_12mo} onChange={set("success_12mo")} data-testid="cl-input-success_12mo" /></Field>
                                <Field label="What happens if nothing changes in 12 months?"><textarea className="field-input" rows={4} value={form.if_nothing_changes} onChange={set("if_nothing_changes")} data-testid="cl-input-if_nothing_changes" /></Field>
                            </div>
                        )}
                        {step === 4 && (
                            <div className="space-y-10">
                                <Field label="Current Revenue Range">
                                    <div className="flex flex-wrap gap-2">
                                        {REVENUE.map((r) => (
                                            <button key={r} type="button" onClick={() => setForm((s) => ({ ...s, revenue_range: r }))} className={`tag ${form.revenue_range === r ? "is-active" : ""}`} data-testid={`cl-revenue-${r}`}>{r}</button>
                                        ))}
                                    </div>
                                </Field>
                                <Field label="Marketing Investment Range">
                                    <div className="flex flex-wrap gap-2">
                                        {INVESTMENTS.map((r) => (
                                            <button key={r} type="button" onClick={() => setForm((s) => ({ ...s, investment_range: r }))} className={`tag ${form.investment_range === r ? "is-active" : ""}`} data-testid={`cl-invest-${r}`}>{r}</button>
                                        ))}
                                    </div>
                                </Field>
                                <Field label="Additional Notes"><textarea className="field-input" rows={3} value={form.additional_notes} onChange={set("additional_notes")} data-testid="cl-input-additional_notes" /></Field>
                                <Field label="Brand documents (optional)"><FileUpload category="client" value={files} onChange={setFiles} testid="cl-upload" /></Field>
                            </div>
                        )}

                        <div className="hairline my-10" />

                        <div className="flex items-center justify-between flex-wrap gap-4">
                            <div className="font-mono text-xs text-white/55">
                                {String(step + 1).padStart(2, "0")} <span className="text-white/30">/ 0{STEPS.length}</span> · {STEPS[step].label}
                            </div>
                            <div className="flex gap-3 items-center">
                                {!user && step === STEPS.length - 1 && (
                                    <span className="text-xs text-white/55 flex items-center gap-1.5" data-testid="cl-signin-hint">
                                        <Lock size={12} className="text-saffron-brand" /> Sign in required to submit
                                    </span>
                                )}
                                {step > 0 && <button onClick={() => setStep((s) => s - 1)} className="btn-secondary" data-testid="cl-prev-btn">Back</button>}
                                {step < STEPS.length - 1 ? (
                                    <button onClick={next} className="btn-primary" data-testid="cl-next-btn">Continue <ArrowUpRight size={16} /></button>
                                ) : (
                                    <button onClick={submit} disabled={busy} className="btn-primary" data-testid="cl-submit-btn">
                                        {busy ? <Loader2 size={16} className="animate-spin" /> : <ArrowUpRight size={16} />}
                                        {user ? "Submit Application" : "Sign in & Submit"}
                                    </button>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            <Footer />
        </main>
    );
}
